import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { save, remove, create, upload } from '../../../lib/fetch/profiles';
import { deleteFile, getSignedUrl } from '../../../lib/fetch/files';
import ProfileInfoPresentational from './ProfileInfoPresentational';
import { useLoading } from '../../../commons/Loading';
import { useAuth } from '../../../commons/Auth';
import { list as listAffiliations } from '../../../lib/fetch/affiliations';
import moment from 'moment';
import axios from 'axios';
import { AdminTypes } from '../../../lib/constants';

const ProfileInfo = (props) => {
  const [{ newToken } = {}] = useAuth();
  const [t] = useTranslation();
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();
  const [activeAffiliation, setActiveAffiliation] = React.useState({});
  const [activities, setActivities] = useState([]);
  const [softwares, setSoftwares] = useState([]);

  const {
    history,
    id,
    profile,
    setProfile,
    managers,
    referringAgencies,
    fetchProfile,
  } = props;

  const hasNetwork = false;

  const isUpdating = id !== 'create';

  async function updateProfile(updates) {
    showLoading();

    const { error, data } = await save({ id, data: updates, newToken });

    if (error) {
      hideLoading();

      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    fetchProfile(id);
    hideLoading();
    alert.success(t('COMMON:SUCCESS_UPDATE'));
  }

  async function deleteProfile() {
    showLoading();
    const { error } = await remove({ id, newToken });

    if (error) {
      hideLoading();
      const { response = {} } = error;
      const { data = {} } = response;
      const { message = '' } = data;
      alert.error(message || t('COMMON:GENERIC_ERROR'));
      return;
    }

    setProfile({});
    hideLoading();
    alert.success(t('COMMON:SUCCESS_DELETE'));
    history.push('/profiles');
  }

  async function createProfile(profile) {
    showLoading();
    const { error, data } = await create({ id, data: profile, newToken });

    if (error) {
      hideLoading();

      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();

    alert.success(t('COMMON:SUCCESS_CREATE'));
    history.replace(`/profiles/${data._id}/info`);
  }

  async function uploadFile(file) {
    showLoading();

    const { error, data } = await upload({ id, file, newToken });

    if (error) {
      hideLoading();

      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();
    setProfile({ ...profile, ...data });
    alert.success(t('COMMON:SUCCESS_UPLOAD'));
  }

  async function handleDelete({ key, field }) {
    showLoading();

    const { error, data } = await deleteFile({ id, key, field, newToken });

    if (error) {
      hideLoading();

      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();
    setProfile({ ...profile, ...data });
    alert.success(t('COMMON:SUCCESS_DELETE'));
  }

  async function handleFileListView(key) {
    showLoading();

    const { data, error } = await getSignedUrl(key, newToken);

    if (error) {
      hideLoading();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();
    window.open(data.url, '_blank');
  }

  const fetchAffiliations = async () => {
    showLoading();

    const { error, data } = await listAffiliations({
      profileId: profile._id,
      newToken,
    });

    if (error) {
      hideLoading();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    const activeAffiliations = data.filter((affiliation) => {
      const today = moment();
      const endData = moment(affiliation.endDate);
      return endData.isAfter(today);
    });

    setActiveAffiliation(activeAffiliations[0] || {});
    hideLoading();
  };

  const fetchActivities = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/activities`,
      {
        headers: { Authorization: newToken },
        params: {
          limit: 9999,
          offset: 0,
          name: '',
          profileType:
            profile.type === 'SUPPLIER'
              ? ''
              : AdminTypes.includes(profile.type)
              ? 'ADMIN'
              : profile.type,
        },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [], headers = {} } = response;

    setActivities(data.activities);
  };

  const fetchSoftwares = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/softwares`,
      {
        headers: { Authorization: newToken },
      }
    );

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    let { data = [], headers = {} } = response;
    setSoftwares(data);
  };

  React.useEffect(() => {
    if (profile._id) fetchAffiliations();
    fetchActivities();
    fetchSoftwares();
  }, [profile]);

  return (
    <ProfileInfoPresentational
      data={profile}
      isUpdating={isUpdating}
      updateProfile={updateProfile}
      deleteProfile={deleteProfile}
      createProfile={createProfile}
      uploadFile={uploadFile}
      handleDelete={handleDelete}
      handleFileListView={handleFileListView}
      activeAffiliation={activeAffiliation}
      managers={managers}
      softwares={softwares}
      referringAgencies={referringAgencies}
      activities={activities}
      {...props}
    />
  );
};

export default ProfileInfo;
